/**
 * RefundDialog — Phase A3
 *
 * Shows the policy-allowed cap + formula breakdown (paid - fee -
 * consumed - already_refunded) and lets the merchant initiate a Stripe
 * refund. request_uuid is generated client-side so retries are
 * idempotent end-to-end.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { billing, type PackagePurchase, type RefundPreview } from '../api/client'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

interface RefundDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  purchase: PackagePurchase | null
  onRefunded: () => void
}

function fmtMoney(minor: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(minor / 100)
  } catch {
    return `${currency} ${minor / 100}`
  }
}

// Web Crypto UUID — falls back to time+rand if unavailable.
function newRequestUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `r${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export function RefundDialog({
  open,
  onOpenChange,
  purchase,
  onRefunded,
}: RefundDialogProps) {
  const { t, i18n } = useTranslation()
  const [preview, setPreview] = useState<RefundPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [amount, setAmount] = useState<number>(0)
  const [reason, setReason] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [requestUuid] = useState(() => newRequestUuid())

  useEffect(() => {
    if (!open || !purchase) {
      setPreview(null)
      setPreviewError(null)
      setAmount(0)
      setReason('')
      setSubmitError(null)
      return
    }
    let alive = true
    billing
      .getRefundPreview(purchase.id)
      .then((p) => {
        if (!alive) return
        setPreview(p)
        setAmount(p.allowed_minor) // default to max allowed
      })
      .catch(() => alive && setPreviewError('preview_failed'))
    return () => {
      alive = false
    }
  }, [open, purchase])

  async function submit() {
    if (!purchase || !preview) return
    if (amount <= 0) {
      setSubmitError('amount_must_be_positive')
      return
    }
    if (amount > preview.allowed_minor) {
      setSubmitError('amount_exceeds_allowed')
      return
    }
    if (!reason.trim()) {
      setSubmitError('reason_required')
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      await billing.refundPurchase(purchase.id, {
        amount_minor: amount,
        reason: reason.trim(),
        request_uuid: requestUuid,
      })
      onRefunded()
    } catch (e) {
      const detail = (e as { detail?: string; message?: string })?.detail
      const message = (e as { message?: string })?.message
      setSubmitError(detail ?? message ?? 'refund_failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('packages.refund.title')}</DialogTitle>
          <DialogDescription>
            {purchase && (
              <>
                {i18n.language?.toLowerCase().startsWith('zh')
                  ? purchase.snapshot_name_zh
                  : purchase.snapshot_name_en}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {previewError && (
          <p className="text-sm text-destructive">
            {t('packages.refund.previewFailed')}
          </p>
        )}

        {preview && purchase && (
          <div className="space-y-3" data-testid="refund-preview">
            <div className="rounded border bg-muted/30 p-3 text-sm">
              <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                {t('packages.refund.formula')}
              </div>
              <div className="space-y-1 font-mono text-xs">
                <div className="flex justify-between">
                  <span>{t('packages.refund.paid')}</span>
                  <span>{fmtMoney(preview.amount_paid_minor, purchase.currency, i18n.language)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>− {t('packages.refund.fee')}</span>
                  <span>{fmtMoney(preview.stripe_fee_minor, purchase.currency, i18n.language)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>
                    − {t('packages.refund.consumed', {
                      sessions: preview.sessions_used,
                      unit: fmtMoney(preview.snapshot_refund_unit_value_minor, purchase.currency, i18n.language),
                    })}
                  </span>
                  <span>
                    {fmtMoney(
                      preview.sessions_used * preview.snapshot_refund_unit_value_minor,
                      purchase.currency,
                      i18n.language,
                    )}
                  </span>
                </div>
                {preview.amount_refunded_minor > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>− {t('packages.refund.alreadyRefunded')}</span>
                    <span>{fmtMoney(preview.amount_refunded_minor, purchase.currency, i18n.language)}</span>
                  </div>
                )}
                <div className="border-t pt-1 flex justify-between font-medium">
                  <span>= {t('packages.refund.allowed')}</span>
                  <span data-testid="refund-allowed">
                    {fmtMoney(preview.allowed_minor, purchase.currency, i18n.language)}
                  </span>
                </div>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {t(`packages.refundPolicy.${preview.snapshot_refund_policy_mode}`)}
              </div>
            </div>

            <div>
              <Label htmlFor="refund-amount">
                {t('packages.refund.amountLabel', {
                  currency: purchase.currency,
                })}
              </Label>
              <Input
                id="refund-amount"
                type="number"
                min={0}
                max={preview.allowed_minor}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value) || 0)}
                data-testid="refund-amount"
              />
            </div>
            <div>
              <Label htmlFor="refund-reason">
                {t('packages.refund.reasonLabel')}
              </Label>
              <Input
                id="refund-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('packages.refund.reasonPlaceholder') as string}
                data-testid="refund-reason"
              />
            </div>
            {submitError && (
              <p className="text-sm text-destructive" data-testid="refund-error">
                {t(`packages.refund.errors.${submitError}`, submitError)}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('packages.refund.cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting || !preview} data-testid="refund-submit">
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('packages.refund.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
